import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle2, XCircle, RefreshCw, Search, Globe, Clock, Settings, History, Save } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { toast } from "sonner";

interface SeoStatus {
  site_url: string;
  expected_verification_token: string | null;
  checked_at: string;
  verification_token_match?: boolean;
  live_page?: {
    status?: number;
    ok?: boolean;
    last_modified?: string | null;
    meta?: {
      google_site_verification: string | null;
      description: string | null;
      canonical: string | null;
      title: string | null;
      robots: string | null;
    };
    has_jsonld?: boolean;
    error?: string;
  };
  gsc?: {
    connected?: boolean;
    verified?: boolean;
    permission_level?: string | null;
    site_count?: number;
    error?: string;
  };
}

interface HistoryRow {
  id: string;
  checked_at: string;
  http_status: number | null;
  gsc_connected: boolean | null;
  gsc_verified: boolean | null;
  verification_token_match: boolean | null;
  verification_token_found: string | null;
  has_jsonld: boolean | null;
  meta_description: string | null;
  meta_canonical: string | null;
  last_modified: string | null;
}

const StatusBadge = ({ ok, label }: { ok: boolean; label: string }) => (
  <Badge
    className={
      ok
        ? "bg-green-500/10 text-green-700 border-green-500/20"
        : "bg-red-500/10 text-red-700 border-red-500/20"
    }
  >
    {ok ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
    {label}
  </Badge>
);

const Row = ({ label, value, ok }: { label: string; value: React.ReactNode; ok?: boolean }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b last:border-0">
    <div className="text-sm font-medium text-muted-foreground min-w-[180px]">{label}</div>
    <div className="text-sm flex-1 break-all">
      {ok !== undefined && <StatusBadge ok={ok} label={ok ? "OK" : "Fehlt"} />}
      <div className="mt-1 text-foreground">{value || <span className="text-muted-foreground">—</span>}</div>
    </div>
  </div>
);

const Tick = ({ ok }: { ok: boolean | null }) =>
  ok === null ? (
    <span className="text-muted-foreground">—</span>
  ) : ok ? (
    <CheckCircle2 className="h-4 w-4 text-green-600 inline" />
  ) : (
    <XCircle className="h-4 w-4 text-red-600 inline" />
  );

const SeoStatus = () => {
  const [data, setData] = useState<SeoStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Settings
  const [token, setToken] = useState("");
  const [siteUrl, setSiteUrl] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  // History
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchSettings = async () => {
    const { data } = await supabase
      .from("seo_settings")
      .select("expected_verification_token, site_url")
      .eq("id", "singleton")
      .maybeSingle();
    if (data) {
      setToken(data.expected_verification_token ?? "");
      setSiteUrl(data.site_url ?? "");
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    const { data } = await supabase
      .from("seo_status_checks")
      .select(
        "id, checked_at, http_status, gsc_connected, gsc_verified, verification_token_match, verification_token_found, has_jsonld, meta_description, meta_canonical, last_modified",
      )
      .order("checked_at", { ascending: false })
      .limit(50);
    setHistory((data || []) as HistoryRow[]);
    setLoadingHistory(false);
  };

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke("seo-status");
      if (error) throw error;
      setData(data as SeoStatus);
      fetchHistory();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    const { data: userRes } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("seo_settings")
      .update({
        expected_verification_token: token || null,
        site_url: siteUrl || "https://eigenheimverrentung.de/",
        updated_at: new Date().toISOString(),
        updated_by: userRes.user?.id ?? null,
      })
      .eq("id", "singleton");
    setSavingSettings(false);
    if (error) {
      toast.error("Speichern fehlgeschlagen: " + error.message);
    } else {
      toast.success("Einstellungen gespeichert");
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchStatus();
  }, []);

  const meta = data?.live_page?.meta;
  const verificationOk = !!data?.verification_token_match;
  const lastModified = data?.live_page?.last_modified;

  return (
    <div className="space-y-6">
      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" /> SEO-Einstellungen
          </CardTitle>
          <CardDescription>
            Konfiguriere den erwarteten Google-Site-Verification-Token und die zu prüfende URL.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="site-url">Site-URL</Label>
              <Input
                id="site-url"
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                placeholder="https://eigenheimverrentung.de/"
              />
            </div>
            <div>
              <Label htmlFor="token">Erwarteter Verification-Token</Label>
              <Input
                id="token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="z. B. 1WNxs4SkwQ..."
              />
            </div>
          </div>
          <Button onClick={saveSettings} disabled={savingSettings}>
            <Save className="h-4 w-4 mr-2" /> Speichern
          </Button>
        </CardContent>
      </Card>

      {/* Live status */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              SEO-Verifizierung
            </CardTitle>
            <CardDescription>
              Live-Status der Seite, GSC-Verbindung und Meta-Tags
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchStatus} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Prüfen
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="text-sm text-destructive">{error}</div>
          ) : data ? (
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Search className="h-4 w-4" /> Google Search Console
                </h3>
                {data.gsc?.error ? (
                  <div className="text-sm text-destructive">{data.gsc.error}</div>
                ) : (
                  <div>
                    <Row label="Verbindung" ok={!!data.gsc?.connected} value={data.gsc?.connected ? "Aktiv" : "Nicht verbunden"} />
                    <Row
                      label="Site verifiziert"
                      ok={!!data.gsc?.verified}
                      value={data.gsc?.verified ? `${data.site_url} (${data.gsc?.permission_level || "—"})` : "Site nicht in GSC gefunden"}
                    />
                    <Row label="Properties insgesamt" value={String(data.gsc?.site_count ?? 0)} />
                  </div>
                )}
              </div>

              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Globe className="h-4 w-4" /> Live-Seite ({data.site_url})
                </h3>
                {data.live_page?.error ? (
                  <div className="text-sm text-destructive">{data.live_page.error}</div>
                ) : (
                  <div>
                    <Row label="HTTP-Status" ok={!!data.live_page?.ok} value={String(data.live_page?.status)} />
                    <Row
                      label="Verification-Tag"
                      ok={verificationOk}
                      value={
                        meta?.google_site_verification
                          ? meta.google_site_verification +
                            (data.expected_verification_token && !verificationOk
                              ? ` (erwartet: ${data.expected_verification_token})`
                              : "")
                          : "Nicht gefunden"
                      }
                    />
                    <Row label="Title" ok={!!meta?.title} value={meta?.title} />
                    <Row label="Description" ok={!!meta?.description} value={meta?.description} />
                    <Row label="Canonical" ok={!!meta?.canonical} value={meta?.canonical} />
                    <Row label="JSON-LD" ok={!!data.live_page?.has_jsonld} value={data.live_page?.has_jsonld ? "Vorhanden" : "Fehlt"} />
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Letzte Publizierung
                </h3>
                <Row
                  label="Last-Modified Header"
                  value={
                    lastModified
                      ? `${lastModified} (${format(new Date(lastModified), "dd.MM.yyyy HH:mm", { locale: de })})`
                      : "Nicht verfügbar"
                  }
                />
                <Row label="Geprüft am" value={format(new Date(data.checked_at), "dd.MM.yyyy HH:mm:ss", { locale: de })} />
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" /> Verlauf (letzte 50 Prüfungen)
          </CardTitle>
          <CardDescription>
            Zeitreihe von GSC-Verbindung, Verifizierung und Meta-Tag-Präsenz.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <Skeleton className="h-40 w-full" />
          ) : history.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              Noch keine Prüfungen aufgezeichnet. Klicke „Prüfen", um die erste Aufzeichnung anzulegen.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Zeitpunkt</TableHead>
                    <TableHead>HTTP</TableHead>
                    <TableHead className="text-center">GSC verbunden</TableHead>
                    <TableHead className="text-center">GSC verifiziert</TableHead>
                    <TableHead className="text-center">Token-Match</TableHead>
                    <TableHead className="text-center">Description</TableHead>
                    <TableHead className="text-center">Canonical</TableHead>
                    <TableHead className="text-center">JSON-LD</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap">
                        {format(new Date(row.checked_at), "dd.MM.yy HH:mm", { locale: de })}
                      </TableCell>
                      <TableCell>{row.http_status ?? "—"}</TableCell>
                      <TableCell className="text-center"><Tick ok={row.gsc_connected} /></TableCell>
                      <TableCell className="text-center"><Tick ok={row.gsc_verified} /></TableCell>
                      <TableCell className="text-center"><Tick ok={row.verification_token_match} /></TableCell>
                      <TableCell className="text-center"><Tick ok={!!row.meta_description} /></TableCell>
                      <TableCell className="text-center"><Tick ok={!!row.meta_canonical} /></TableCell>
                      <TableCell className="text-center"><Tick ok={row.has_jsonld} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SeoStatus;
